/**
 * `sgGroup(id).leaderboard.*` — client sync facade for the per-card MP
 * push + the live ack cache.
 *
 * Strictly the WS slice of the leaderboard. The REST snapshot /
 * `use({ period })` / `invalidate()` methods deliberately do NOT live
 * here — shared makes no HTTP calls anywhere under `sync-client/`, and
 * `flashka-web/src/lib/leaderboard/` is where the fetch + auth headers
 * + URL base belong. Splitting on that line keeps shared transport-
 * agnostic; mobile will bring its own HTTP client in a follow-up rather
 * than reaching through a leaky shared abstraction.
 *
 * ### Per-group streak self-read
 * Each linked group has its OWN server-authoritative streak (the
 * `sg-score-ack` for that group bumps `liveScore[gid].streak`). A card
 * studied on a deck shared in two groups therefore credits two DIFFERENT
 * centi-MP integers — one per group's pre-bump multiplier. `pushScore`
 * accepts only `{ cardId, eventType }`: it reads the group's own streak
 * from the Zustand store, computes the breakdown via `scoreFor`, sends
 * the wire, and returns the breakdown so the caller can feed it into
 * the in-session accumulator + toast pipeline.
 */

import { localDayKey } from '../activity/derive';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { sendSG } from './helpers';
import type { ScoreBreakdown, ScoreEventType } from './scoring';
import {
  effectiveStreak,
  scoreFor,
  scoreForCaseStudySession,
  scoreForOralExamSession,
} from './scoring';
import { takeStudyTimeDeltaMsForWire } from './study-time';

/**
 * In-memory cache of the latest server ack for one group. Populated by
 * the `sg-score-ack` inbound handler in `study-group-do-adapter.ts`;
 * read synchronously by `liveScore()` so callers (toast logic, summary
 * popover, the next `pushScore` choosing a multiplier) see the
 * server-authoritative values.
 *
 * `totalMP` and `todayMP` are CENTI-MP integers — see module doc.
 */
export type LiveScoreCache = {
  /** Post-bump streak in days. Use for the NEXT card's multiplier. */
  streak: number;
  /** Lifetime total, centi-MP. */
  totalMP: number;
  /** User-local today total, centi-MP. */
  todayMP: number;
  /** Wall-clock ISO at which the ack was received (client clock). */
  lastUpdatedAtISO: string;
};

export type PushScoreArgs = {
  cardId: string;
  eventType: ScoreEventType;
};

export type LeaderboardApi = {
  /**
   * Fire-and-forget per-card push over the existing WS sender. The
   * facade reads THIS group's pre-bump streak from the store
   * (`liveScore.streak ?? 0`), computes the centi-MP delta via
   * `scoreFor(eventType, streak)`, sends `sg-score-delta`, and
   * returns the breakdown for caller-side accumulation.
   *
   * The server's `applyScoreDelta` stamps `userId` from auth, applies
   * lazy week + day resets, bumps streak (max once per user-local day),
   * and acks via `sg-score-ack` (which the adapter routes into the
   * store's `liveScore` slice).
   *
   * Offline-safe: `sendSG` queues to the outbox when disconnected.
   * Caller does NOT await.
   */
  pushScore(args: PushScoreArgs): ScoreBreakdown;
  /**
   * Synchronous read of the last server-acked scalars for this group.
   * `undefined` until the first ack arrives. Read BEFORE the next
   * `pushScore` so the multiplier choice matches the server-authoritative
   * streak — the first card of a new local day uses the pre-bump
   * multiplier; subsequent cards use the post-bump multiplier.
   */
  liveScore(): LiveScoreCache | undefined;
};

export function createLeaderboardApi(groupId: string): LeaderboardApi {
  return {
    pushScore({ cardId, eventType }) {
      const now = new Date();
      const userLocalDayISO = localDayKey(now);
      const cache = useStudyGroupStore.getState().groups[groupId]?.liveScore;
      // Cached streak goes stale across multi-day gaps — derive the
      // effective value from the cache's own day anchor (the local day
      // when the last ack arrived) so the first card of a resumed session
      // picks the ×1.0 multiplier instead of the inflated pre-gap one.
      const streak = cache
        ? effectiveStreak(
            {
              streak: cache.streak,
              lastStudyLocalDayISO: localDayKey(new Date(cache.lastUpdatedAtISO)),
            },
            userLocalDayISO,
          )
        : 0;
      const breakdown = scoreFor(eventType, streak);
      // Piggyback the pending study-time credit (single-group variant of
      // the drain in `pushScoreToAllGroups` — see study-time.ts).
      const timeDeltaMs = takeStudyTimeDeltaMsForWire();
      sendSG(groupId, {
        type: 'sg-score-delta',
        delta: breakdown.centiMP,
        cardId,
        eventType,
        userLocalDayISO,
        utcAtISO: now.toISOString(),
        ...(timeDeltaMs > 0 ? { timeDeltaMs } : {}),
      });
      return breakdown;
    },
    liveScore() {
      return useStudyGroupStore.getState().groups[groupId]?.liveScore;
    },
  };
}

/**
 * Fan-out helper for decks shared in N groups. Each linked group has
 * its OWN pre-bump streak (per-group multiplier), so the per-card delta
 * differs per group — this loop reads each group's `liveScore.streak`,
 * computes the breakdown locally, and sends a wire with the group's
 * own integer centi-MP delta. Per-group failures are isolated: a single
 * bad sender (e.g. provider unregistered for one of the groups) MUST
 * NOT abort the loop.
 *
 * Returns the per-group `ScoreBreakdown` map keyed by groupId — callers
 * (the in-session accumulator + toast pipeline) consume this to track
 * rawSum/sessionMP per group AND drive multiplier-based toast triggers.
 * A throwing send for `g` omits `g` from the returned map.
 *
 * Empty `deckLinkedGroupIds` → no-op, empty record. The caller is
 * responsible for gating on `deck.linkedGroups?.length > 0` — private
 * decks skip pushScore entirely.
 *
 * Timestamps are hoisted out of the loop so all groups see the same
 * `userLocalDayISO` / `utcAtISO` within a single call — the entire
 * loop runs synchronously, so this is identical to capturing inside
 * each push, but cheaper.
 */
export function pushScoreToAllGroups(
  deckLinkedGroupIds: readonly string[],
  args: PushScoreArgs,
): Record<string, ScoreBreakdown> {
  const result: Record<string, ScoreBreakdown> = {};
  if (deckLinkedGroupIds.length === 0) return result;
  const now = new Date();
  const userLocalDayISO = localDayKey(now);
  const utcAtISO = now.toISOString();
  // Drain the ambient study-time tracker ONCE per card event; every
  // linked group receives the same ms credit — full credit per group,
  // mirroring how each group receives the card's full MP delta. The
  // per-session oral-exam / case-study pushers below deliberately do
  // NOT drain: their time flows via the periodic `sg-time-delta` flush,
  // and a drain here would be lost when a 0% session skips the send.
  const timeDeltaMs = takeStudyTimeDeltaMsForWire();
  const state = useStudyGroupStore.getState();
  for (const groupId of deckLinkedGroupIds) {
    try {
      const cache = state.groups[groupId]?.liveScore;
      const streak = cache
        ? effectiveStreak(
            {
              streak: cache.streak,
              lastStudyLocalDayISO: localDayKey(new Date(cache.lastUpdatedAtISO)),
            },
            userLocalDayISO,
          )
        : 0;
      const breakdown = scoreFor(args.eventType, streak);
      sendSG(groupId, {
        type: 'sg-score-delta',
        delta: breakdown.centiMP,
        cardId: args.cardId,
        eventType: args.eventType,
        userLocalDayISO,
        utcAtISO,
        ...(timeDeltaMs > 0 ? { timeDeltaMs } : {}),
      });
      result[groupId] = breakdown;
    } catch (err) {
      // Per-group isolation — one failed sender (e.g. unregistered
      // provider) MUST NOT abort the remaining groups. Log + continue.

      console.warn('[sg-leaderboard] pushScore failed for group', groupId, err);
    }
  }
  return result;
}

export type PushOralExamSessionArgs = {
  /** Final score percent (0..100). Clamped inside `scoreForOralExamSession`. */
  percent: number;
  /** Unique session identifier — passed as `cardId` on the wire so the
   *  server's per-event log stays consistent with the per-card pattern.
   *  Use the oral-exam attempt timestamp (`feedbackAttemptTsRef`). */
  sessionId: string;
};

/**
 * Per-SESSION sibling of `pushScoreToAllGroups`. One push per linked
 * group when an oral exam finishes; each group's pre-bump streak drives
 * its own multiplier via `scoreForOralExamSession`, so a perfect run on
 * a deck shared in two groups credits two DIFFERENT centi-MP integers
 * (matching the per-card pattern above).
 *
 * Returns the per-group `ScoreBreakdown` map so the caller can pick the
 * entry-group's MP for the toast / accumulator. Empty input → no-op,
 * empty record (caller should already have skipped when
 * `deck.linkedGroups` is empty).
 *
 * Zero-percent sessions are dropped at the data layer: the server's
 * `applyScoreDelta` rejects `delta <= 0` with `ScoreDeltaInvalid`
 * (Sentry-noise + `sg-sync-error` back to the client), so we skip the
 * send entirely when the breakdown comes out to 0 centi-MP. Today the
 * call site already guards in `pushOralExamSessionMp` (returns 0 on
 * `percent <= 0` before fanning out), so this is a belt-and-braces
 * guard — but it keeps the contract honest for future callers. The
 * accumulator + toast UX still wants nothing visible on a wipeout, so
 * callers MUST gate their own visuals at their layer.
 */
export function pushOralExamSessionToAllGroups(
  deckLinkedGroupIds: readonly string[],
  args: PushOralExamSessionArgs,
): Record<string, ScoreBreakdown> {
  const result: Record<string, ScoreBreakdown> = {};
  if (deckLinkedGroupIds.length === 0) return result;
  const now = new Date();
  const userLocalDayISO = localDayKey(now);
  const utcAtISO = now.toISOString();
  const state = useStudyGroupStore.getState();
  for (const groupId of deckLinkedGroupIds) {
    try {
      const cache = state.groups[groupId]?.liveScore;
      // Mirror the per-card path: derive the effective streak from the
      // cache's own day anchor so an oral exam taken as the first
      // action of a resumed session after a ≥2-day gap picks the ×1.0
      // multiplier instead of the inflated pre-gap one.
      const streak = cache
        ? effectiveStreak(
            {
              streak: cache.streak,
              lastStudyLocalDayISO: localDayKey(new Date(cache.lastUpdatedAtISO)),
            },
            userLocalDayISO,
          )
        : 0;
      const breakdown = scoreForOralExamSession(args.percent, streak);
      // Server rejects delta <= 0 — skip the send rather than producing
      // Sentry noise + an sg-sync-error frame. Result map still omits
      // the group, matching the "no MP credited" semantics.
      if (breakdown.centiMP <= 0) continue;
      sendSG(groupId, {
        type: 'sg-score-delta',
        delta: breakdown.centiMP,
        cardId: args.sessionId,
        eventType: 'oral_exam_session',
        userLocalDayISO,
        utcAtISO,
      });
      result[groupId] = breakdown;
    } catch (err) {
      // Per-group isolation — see `pushScoreToAllGroups` above.

      console.warn('[sg-leaderboard] pushOralExamSession failed for group', groupId, err);
    }
  }
  return result;
}

export type PushCaseStudySessionArgs = {
  /** Final case-study score percent (0..100). Clamped inside
   *  `scoreForCaseStudySession`. */
  percent: number;
  /** Unique session identifier — passed as `cardId` on the wire so the
   *  server's per-event log stays consistent with the per-card and
   *  oral-exam-session patterns. Use the case-study attempt id
   *  (`quizTs`) so re-renders on the results screen can't double-push. */
  sessionId: string;
};

/**
 * Per-SESSION sibling of `pushOralExamSessionToAllGroups`. Same
 * fan-out shape — one push per linked group at case-study completion,
 * each group's own pre-bump streak drives its own multiplier via
 * `scoreForCaseStudySession`, so a perfect run on a deck shared in two
 * groups credits two DIFFERENT centi-MP integers.
 *
 * Returns the per-group `ScoreBreakdown` map so the caller can pick
 * the entry-group's MP for the toast / accumulator. Empty input →
 * no-op, empty record (caller should already have skipped when
 * `deck.linkedGroups` is empty).
 *
 * Zero-percent sessions are dropped at the data layer for the same
 * reason as `pushOralExamSessionToAllGroups`: the server rejects
 * `delta <= 0` with `ScoreDeltaInvalid` (Sentry-noise + sync-error), so
 * we skip the send when the breakdown comes out to 0 centi-MP.
 * Callers still gate their own toast / reveal visuals — the "no MP
 * credited" UX is the caller's responsibility.
 */
export function pushCaseStudySessionToAllGroups(
  deckLinkedGroupIds: readonly string[],
  args: PushCaseStudySessionArgs,
): Record<string, ScoreBreakdown> {
  const result: Record<string, ScoreBreakdown> = {};
  if (deckLinkedGroupIds.length === 0) return result;
  const now = new Date();
  const userLocalDayISO = localDayKey(now);
  const utcAtISO = now.toISOString();
  const state = useStudyGroupStore.getState();
  for (const groupId of deckLinkedGroupIds) {
    try {
      const cache = state.groups[groupId]?.liveScore;
      const streak = cache
        ? effectiveStreak(
            {
              streak: cache.streak,
              lastStudyLocalDayISO: localDayKey(new Date(cache.lastUpdatedAtISO)),
            },
            userLocalDayISO,
          )
        : 0;
      const breakdown = scoreForCaseStudySession(args.percent, streak);
      if (breakdown.centiMP <= 0) continue;
      sendSG(groupId, {
        type: 'sg-score-delta',
        delta: breakdown.centiMP,
        cardId: args.sessionId,
        eventType: 'case_study_session',
        userLocalDayISO,
        utcAtISO,
      });
      result[groupId] = breakdown;
    } catch (err) {
      // Per-group isolation — see `pushScoreToAllGroups` above.

      console.warn(
        '[sg-leaderboard] pushCaseStudySession failed for group',
        groupId,
        err,
      );
    }
  }
  return result;
}
